import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { slotService } from "@/services/slotService";
import { Slot } from "@/types/slot";
import { useOrganization } from "../../App";
import { format, parseISO } from "date-fns";
import { Calendar, Plus, Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SlotForm } from "./SlotForm";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import { NoData } from "@/components/ui/no-data";
import { LoadingSpinner } from "@/components/ui/loading-spinner";

interface SlotsListProps {
  campaignId?: string;
}

export function SlotsList({ campaignId }: SlotsListProps) {
  const [slots, setSlots] = useState<Slot[]>([]);
  const [filteredSlots, setFilteredSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const { currentOrganization } = useOrganization();
  const navigate = useNavigate();
  const [date, setDate] = useState(new Date());

  useEffect(() => {
    if (currentOrganization?.id) {
      fetchSlots();
    }
  }, [currentOrganization, campaignId]);

  useEffect(() => {
    filterSlots();
  }, [slots, searchQuery, statusFilter]);

  const fetchSlots = async () => {
    if (!currentOrganization?.id) return;

    setLoading(true);
    try {
      const { data, error } = await slotService.getSlots(
        currentOrganization.id,
        { campaignId },
      );

      if (error) throw error;
      setSlots(data || []);
    } catch (error) {
      console.error("Error fetching slots:", error);
      toast({
        title: "Error",
        description: "Failed to load slots",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const filterSlots = () => {
    let filtered = [...slots];

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((slot) => slot.status === statusFilter);
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (slot) =>
          (slot.name && slot.name.toLowerCase().includes(query)) ||
          (slot.description &&
            slot.description.toLowerCase().includes(query)) ||
          (slot.location && slot.location.toLowerCase().includes(query)),
      );
    }

    setFilteredSlots(filtered);
  };

  const handleCreateSlot = async (newSlot: any) => {
    if (!currentOrganization?.id) return;

    try {
      // If the slot has segments, calculate start_time and end_time from them
      let slotData = { ...newSlot };
      
      if (newSlot.segments && newSlot.segments.length > 0) {
        // Calculate overall start and end times from segments
        const startTimes = newSlot.segments.map((s: any) => new Date(s.start_time).getTime());
        const endTimes = newSlot.segments.map((s: any) => new Date(s.end_time).getTime());
        slotData.start_time = new Date(Math.min(...startTimes)).toISOString();
        slotData.end_time = new Date(Math.max(...endTimes)).toISOString();
      } else {
        // Default to a 1-hour slot starting now if no segments
        const now = new Date();
        slotData.start_time = now.toISOString();
        slotData.end_time = new Date(now.getTime() + 60 * 60 * 1000).toISOString();
      }

      // Add organization_id
      slotData.organization_id = currentOrganization.id;

      if (
        newSlot.recurrence_pattern &&
        newSlot.recurrence_pattern !== "none" &&
        newSlot.recurrence_end_date
      ) {
        // Generate recurring slots
        const recurringSlots = slotService.generateRecurringSlots(
          slotData,
          newSlot.recurrence_pattern,
          newSlot.recurrence_end_date,
        );

        await slotService.createMultipleSlots(recurringSlots);
      } else {
        await slotService.createSlot(slotData);
      }

      setIsCreateDialogOpen(false);
      fetchSlots();
      toast({
        title: "Slot created",
        description: "The slot has been created successfully",
      });
    } catch (error) {
      console.error("Error creating slot:", error);
      toast({
        title: "Error",
        description: "Failed to create slot",
        variant: "destructive",
      });
    }
  };

  const handleSlotClick = (slot: Slot) => {
    // Navigate to slot detail page
    navigate(`/slots/${slot.id}`);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "available":
        return (
          <Badge
            variant="outline"
            className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300"
          >
            Available
          </Badge>
        );
      case "full":
        return (
          <Badge
            variant="outline"
            className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300"
          >
            Full
          </Badge>
        );
      case "cancelled":
        return (
          <Badge
            variant="outline"
            className="bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300"
          >
            Cancelled
          </Badge>
        );
      case "archived":
        return (
          <Badge
            variant="outline"
            className="bg-gray-100 dark:bg-gray-800/50 text-gray-800 dark:text-gray-300"
          >
            Archived
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="container mx-auto py-6 bg-background text-foreground">
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
          <div className="flex items-center justify-between space-x-2 mb-6">
            <div className="flex items-center space-x-2">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search slots..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="full">Full</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button  variant= "primaryOutline" onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Slot
            </Button>
          </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner text="Loading slots..." />
          </div>
        ) : slots.length === 0 ? (
          <NoData
            icon={<Calendar className="h-12 w-12 text-muted-foreground/50" />}
            title="No slots created yet"
            description="Get started by creating your first slot."
            actionButton={
              <Button
                onClick={() => setIsCreateDialogOpen(true)}
                variant="primaryOutline"
                className="mt-2"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Slot
              </Button>
            }
          />
        ) : (
          <div className="border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                <TableHead>Name/Description</TableHead>
                <TableHead>Date & Time</TableHead>
                <TableHead>Capacity</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Duration</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSlots.map((slot) => {
                // Get the first segment's start and end time if available
                const segments = slot.segments || [];
                const startTime =
                  segments.length > 0
                    ? parseISO(segments[0].start_time)
                    : new Date();
                const endTime =
                  segments.length > 0
                    ? parseISO(segments[0].end_time)
                    : new Date();
                const availableSeats = slot.capacity - slot.filled_seats;

                return (
                  <TableRow
                    key={slot.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSlotClick(slot)}
                  >
                    <TableCell className="font-medium">{slot.name}</TableCell>
                    <TableCell>
                      <div className="font-medium">
                        {slot.name || "Unnamed Slot"}
                      </div>
                      {slot.description && (
                        <div className="text-sm text-muted-foreground truncate max-w-xs">
                          {slot.description}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="dark:text-white">
                        {format(startTime, "MMM d, yyyy")}
                      </div>
                      <div className="text-sm text-muted-foreground dark:text-gray-300">
                        {format(startTime, "h:mm a")} -{" "}
                        {format(endTime, "h:mm a")}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        {availableSeats}/{slot.capacity} available
                      </div>
                      {slot.filled_seats > 0 && (
                        <div className="text-sm text-muted-foreground">
                          {slot.filled_seats} booked
                        </div>
                      )}
                    </TableCell>
                    <TableCell>{getStatusBadge(slot.status)}</TableCell>
                    <TableCell>{slot.location || "No location"}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Slot</DialogTitle>
            </DialogHeader>
            <SlotForm onSubmit={handleCreateSlot} />
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
